import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  tenantId: string | null;
  tenantSlug: string | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// RLS (auth_tenant_id()) trusts the tenant claim stamped into the access token
// by custom_access_token_hook. session.user.app_metadata is the server-side
// user record, which can disagree (e.g. after set_active_tenant), so read the
// token itself and only fall back to the user record when it can't be decoded.
const decodeTokenAppMetadata = (token: string | undefined): Record<string, unknown> | null => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '='))
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return (JSON.parse(json).app_metadata as Record<string, unknown>) ?? null;
  } catch {
    return null;
  }
};

const extractTenantClaims = (session: Session | null) => {
  const claims = decodeTokenAppMetadata(session?.access_token) ?? session?.user?.app_metadata ?? {};
  return {
    tenantId: (claims.tenant_id as string) ?? null,
    tenantSlug: (claims.tenant_slug as string) ?? null,
  };
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser]             = useState<User | null>(null);
  const [session, setSession]       = useState<Session | null>(null);
  const [loading, setLoading]       = useState(true);
  const [tenantId, setTenantId]     = useState<string | null>(null);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);

  const applySession = (s: Session | null) => {
    setSession(s);
    setUser(s?.user ?? null);
    const { tenantId: tid, tenantSlug: tslug } = extractTenantClaims(s);
    setTenantId(tid);
    setTenantSlug(tslug);
    Sentry.setUser(
      s?.user ? { id: s.user.id, email: s.user.email, tenantId: tid ?? undefined } : null
    );
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        applySession(session);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/onboarding`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: redirectUrl },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setSession(null);
      setUser(null);
      setTenantId(null);
      setTenantSlug(null);
      Sentry.setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, tenantId, tenantSlug, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
